import { StaticImage } from 'gatsby-plugin-image';
import { graphql } from 'gatsby';
import useStoryblok from '~/utils/storyblok-service';
import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import OldTextCard from '~/components/TextCard';
import TextCard from '~/components/Dynamic/TextCard';
import Image from '~/components/Dynamic/Image';
import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import EbookForm from '~/components/Forms/Ebook/EbookForm';
import RichTextRenderer from '~/components/Dynamic/RichTextRenderer';
import RelatedPosts from '~/components/Dynamic/RelatedPosts';

function DownloadTemplate({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story.content;

  console.log(content.downloadFormIdentifier);

  const [showModal, setShowModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const hero = content?.hero[0];

  return (
    <Layout locale={pageContext.locale}>
      <Seo {...content.seo} lang={story.lang} pageContext={pageContext} />
      <section className="-mt-20 text-black bg-beige">
        <div className="container">
          <div className="flex flex-col items-center pt-20 -ml-6 md:pt-36 md:flex-row">
            <div className="flex flex-col w-full pb-12 pl-6 mt-6 md:w-1/2 md:mt-0">
              <TextCard
                className={`md:max-w-lg pt-12 md:pb-32`}
                blok={{
                  ...hero.textCard[0],
                  headingClass: hero.textCard[0]?.headingClass || 'text-54px md:text-h3 lg:text-54px',
                  subHeadingClass: hero.textCard[0]?.subHeadingClass || 'text-black text-sh text-sh--solid',
                  contentClass: hero.textCard[0]?.subHeadingClass || 'text-17px font-medium',
                  tag: 'h1',
                }}
              />

              {content?.downloadLink?.cached_url && (
                <div className="mt-8 mr-auto">
                  <button className="inline-block c-btn c-btn--primary" onClick={() => setShowModal(true)}>
                    {content.downloadLinkTitle || `Download now`}
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center justify-center w-full pl-6 md:w-1/2 md:-mb-20">
              <div className="flex-auto w-full">
                <Image blok={hero.asset[0]?.image} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="my-20 md:my-28">
        <div className="container">
          <div className="flex flex-wrap -ml-16">
            <div className="w-full pl-16 lg:w-6/12">
              <div className="mt-12 md:mt-0 lg:mt-12">
                <RichTextRenderer content={content.long_text} />
              </div>
            </div>
            <div className="w-full pl-16 lg:w-6/12">
              <div className="mx-auto mt-12 md:max-w-md">
                <RichTextRenderer content={content.rhsText} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {content?.darkList?.length > 0 && (
        <section className="bg-black my-28">
          <div className="container py-32">
            <div className="flex">
              <OldTextCard className={`text-white`} heading={content.darkListHeading} />
            </div>
            <div className="flex flex-wrap mt-20 -ml-16">
              {content.darkList.map((li) => (
                <div key={li._uid + 'wrapper'} className="flex flex-col w-full pl-16 mb-12 md:w-1/2 lg:w-1/4">
                  <SbEditable content={li} key={li._uid}>
                    <div>
                      <div>
                        <StaticImage src={`../assets/icons/sparks-blue-white.svg`} alt="sparks blue" width={35} />
                      </div>
                      <div className="font-semibold text-white text-20px font-heading tracking-2">{li.heading}</div>
                    </div>
                  </SbEditable>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <RelatedPosts posts={content?.relatedPosts} />

      <div
        className={`fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-70 z-50 overflow-auto transition-opacity duration-500 ${
          !showModal && 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="container py-20 md:py-40">
          <div className="max-w-[42rem] mx-auto bg-white py-8 px-8 md:px-20 md:py-16 relative">
            <button
              className="absolute w-12 h-12 transition-opacity top-2 right-2 hover:opacity-50"
              onClick={() => setShowModal(false)}
            >
              <div className="w-6 h-0.5 bg-black absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -rotate-45"></div>
              <div className="w-6 h-0.5 bg-black absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
            </button>
            <StaticImage
              className="w-24 mb-4 md:w-40"
              placeholder="tracedSVG"
              src={`../assets/images/ebook/ebook-form-illustration.png`}
              alt=""
            />
            {!isSubmitted ? (
              <>
                <h2 className="text-h2">{content?.downloadFormHeading || 'Download eBook'}</h2>
                <EbookForm
                  setIsSubmitted={setIsSubmitted}
                  downloadFormIdentifier={content.downloadFormIdentifier || content.title}
                />
              </>
            ) : (
              <>
                <h2 className="mb-3 text-h2">{content?.downloadFormSubmittedMessage || 'Thanks!'}</h2>
                <p className="mb-6">
                  {content?.downloadFormSubmittedDescription || 'Click the button below to download your eBook.'}
                </p>
                <div className="flex">
                  <a className="c-btn c-btn--primary" download target="_blank" href={content?.downloadLink?.cached_url}>
                    {content?.downloadLinkTitle || 'Download now'}
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DownloadTemplate;

export const query = graphql`
  query DownloadQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
